import apiClient from "./apiClient";
import { CreateAdminAccountRequest } from "../models/RequestModels/CreateAdminAccountRequest";
import { CreateAdminCleanerRequest } from "../models/RequestModels/CreateAdminCleanerRequest";
import { UpdateAdminSubscriptionRequest } from "../models/RequestModels/UpdateAdminSubscriptionRequest";
import { UpdateAdminUserStatusRequest } from "../models/RequestModels/UpdateAdminUserStatusRequest";
import { AdminAccountResponse } from "../models/ResponseModels/AdminAccountResponse";
import { AdminSubscriptionResponse } from "../models/ResponseModels/AdminSubscriptionResponse";
import { AdminUserResponse } from "../models/ResponseModels/AdminUserResponse";
import { PaginatedResponse } from "../models/ResponseModels/PaginatedResponse";
import { UpsertAdminPropertyRequest } from "../models/RequestModels/UpsertAdminPropertyRequest";
import { UpsertAdminUnitRequest } from "../models/RequestModels/UpsertAdminUnitRequest";
import { AdminPropertyResponse } from "../models/ResponseModels/AdminPropertyResponse";
import { AdminUnitResponse } from "../models/ResponseModels/AdminUnitResponse";

export interface AdminAccountFilters {
  page?: number;
  search?: string;
  status?: string;
  plan?: string;
  per_page?: number;
}

export const getAdminSubscriptionsRequest = async (): Promise<
  AdminSubscriptionResponse[]
> => {
  const response = await apiClient.get<{ data: AdminSubscriptionResponse[] }>(
    "/admin/subscriptions",
  );

  return response.data?.data || [];
};

export const getAdminAccountsRequest = async (
  filters: AdminAccountFilters,
): Promise<PaginatedResponse<AdminAccountResponse>> => {
  const response = await apiClient.get<PaginatedResponse<AdminAccountResponse>>(
    "/admin/accounts",
    { params: filters },
  );

  return response.data;
};

export const getAdminAccountRequest = async (
  accountId: number,
): Promise<AdminAccountResponse> => {
  const response = await apiClient.get<AdminAccountResponse>(
    `/admin/accounts/${accountId}`,
  );

  return response.data;
};

export const createAdminAccountRequest = async (
  body: CreateAdminAccountRequest,
): Promise<AdminAccountResponse> => {
  const response = await apiClient.post<AdminAccountResponse>(
    "/admin/accounts",
    body,
  );

  return response.data;
};

export const updateAdminSubscriptionRequest = async (
  accountId: number,
  body: UpdateAdminSubscriptionRequest,
): Promise<AdminAccountResponse> => {
  const response = await apiClient.patch<AdminAccountResponse>(
    `/admin/accounts/${accountId}/subscription`,
    body,
  );

  return response.data;
};

export const createAdminCleanerRequest = async (
  accountId: number,
  body: CreateAdminCleanerRequest,
): Promise<AdminUserResponse> => {
  const response = await apiClient.post<AdminUserResponse>(
    `/admin/accounts/${accountId}/cleaners`,
    body,
  );

  return response.data;
};

export const updateAdminUserStatusRequest = async (
  userId: number,
  body: UpdateAdminUserStatusRequest,
): Promise<AdminUserResponse> => {
  const response = await apiClient.patch<AdminUserResponse>(
    `/admin/users/${userId}/status`,
    body,
  );

  return response.data;
};

export const deleteAdminUserRequest = async (
  userId: number,
): Promise<AdminAccountResponse | { message: string }> => {
  const response = await apiClient.delete<
    AdminAccountResponse | { message: string }
  >(`/admin/users/${userId}`);

  return response.data;
};

export const deleteAdminAccountRequest = async (
  accountId: number,
): Promise<{ message: string }> => {
  const response = await apiClient.delete<{ message: string }>(
    `/admin/accounts/${accountId}`,
  );

  return response.data;
};

export const createAdminPropertyRequest = async (
  accountId: number,
  body: UpsertAdminPropertyRequest,
): Promise<AdminPropertyResponse> => {
  const response = await apiClient.post<AdminPropertyResponse>(
    `/admin/accounts/${accountId}/properties`,
    body,
  );

  return response.data;
};

export const updateAdminPropertyRequest = async (
  accountId: number,
  propertyId: number,
  body: UpsertAdminPropertyRequest,
): Promise<AdminPropertyResponse> => {
  const response = await apiClient.patch<AdminPropertyResponse>(
    `/admin/accounts/${accountId}/properties/${propertyId}`,
    body,
  );

  return response.data;
};

export const createAdminUnitRequest = async (
  accountId: number,
  body: UpsertAdminUnitRequest,
): Promise<AdminUnitResponse> => {
  const response = await apiClient.post<AdminUnitResponse>(
    `/admin/accounts/${accountId}/units`,
    body,
  );

  return response.data;
};

export const updateAdminUnitRequest = async (
  accountId: number,
  unitId: number,
  body: UpsertAdminUnitRequest,
): Promise<AdminUnitResponse> => {
  const response = await apiClient.patch<AdminUnitResponse>(
    `/admin/accounts/${accountId}/units/${unitId}`,
    body,
  );

  return response.data;
};
